import React, { useState, useEffect } from 'react';
import apiClient from '../services/api';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { Button } from '../components/common/Button';
import StoryDisplay from '../components/exercise/StoryDisplay';
import WordHintsPanel from '../components/exercise/WordHintsPanel';

const STARTER_WORDS = ['improve', 'curious', 'challenge'];

const ExercisePage = () => {
  const [exercise, setExercise] = useState(null);
  const [userAnswers, setUserAnswers] = useState({});
  const [showHints, setShowHints] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [noWords, setNoWords] = useState(false);
  const [isAddingWords, setIsAddingWords] = useState(false);

  useEffect(() => {
    loadExercise();
  }, []);

  const loadExercise = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setNoWords(false);
      const data = await apiClient.generateExercise(5, 10);
      setExercise(data);
      setUserAnswers({});
      setShowHints(false);
    } catch (err) {
      console.error('Exercise generation error:', err);
      if (err.response?.data?.code === 'INSUFFICIENT_WORDS') {
        setNoWords(true);
      } else {
        setError(err.response?.data?.error || 'Failed to generate exercise');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddStarterWords = async () => {
    try {
      setIsAddingWords(true);
      await Promise.all(STARTER_WORDS.map(word => apiClient.createWord(word, true)));
      await loadExercise();
    } catch (err) {
      console.error('Error adding starter words:', err);
      setError('Failed to add starter words. Please try again.');
    } finally {
      setIsAddingWords(false);
    }
  };

  const handleAnswerChange = (blankIndex, value) => {
    setUserAnswers(prev => ({
      ...prev,
      [blankIndex]: value
    }));
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      {(!noWords || isLoading) && (
        <header className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Quick Exercise</h1>
          <p className="text-gray-600 mt-2 flex items-center gap-2">
            {isLoading ? (
              <>
                <svg className="animate-spin h-4 w-4 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Generating exercise, please wait...</span>
              </>
            ) : (
              'Fill in the blanks with your vocabulary words'
            )}
          </p>
        </header>
      )}

      {isLoading && (
        <div className="flex justify-center items-center py-12">
          <LoadingSpinner size="large" />
        </div>
      )}

      {noWords && !isLoading && (
        <div className="text-center py-16 px-6">
          <div className="text-5xl mb-4">📝</div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">You need at least 3 words to start an exercise</h2>
          <p className="text-gray-500 mb-8 max-w-sm mx-auto">
            Add some words to your vocabulary first — or let us add 3 common English words to get you started right away.
          </p>
          <Button
            onClick={handleAddStarterWords}
            disabled={isAddingWords}
            size="lg"
          >
            {isAddingWords ? 'Adding words…' : 'Add 3 starter words & begin'}
          </Button>
          {isAddingWords && (
            <p className="text-sm text-gray-400 mt-3">Adding: {STARTER_WORDS.join(', ')}</p>
          )}
        </div>
      )}

      {error && !noWords && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg mb-4">
          <p className="font-medium">Something went wrong</p>
          <p className="text-sm mt-1">{error}</p>
          <Button onClick={loadExercise} variant="outline" className="mt-3">
            Try Again
          </Button>
        </div>
      )}

      {exercise && !isLoading && (
        <>
          <div className="flex gap-4 mb-4 flex-wrap">
            <Button
              onClick={() => setShowHints(!showHints)}
              variant="outline"
            >
              {showHints ? 'Hide Hints' : 'Show Hints'}
            </Button>
            <Button onClick={loadExercise} variant="secondary">
              New Exercise
            </Button>
          </div>

          {showHints && (
            <WordHintsPanel words={exercise.words} />
          )}

          <StoryDisplay
            key={exercise.exerciseId || exercise.story.join('')}
            story={exercise.story}
            userAnswers={userAnswers}
            onAnswerChange={handleAnswerChange}
            words={exercise.words}
          />
        </>
      )}
    </div>
  );
};

export default ExercisePage;
